import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Sun, Clock, Undo2, ChevronUp, ChevronDown, ArrowLeftRight, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, fmtDate, today } from "@/lib/api";
import { useCentro } from "@/lib/centro";

const days = (d) => (d ? Math.floor((Date.now() - new Date(d).getTime()) / 86400000) : 0);

const SiguienteSelect = ({ d, espera, onSiguiente, onFecha }) => (
  <label className="text-[11px] text-slate-500 flex flex-col gap-0.5">Siguiente que lo coge
    <select data-testid={`device-siguiente-${d.id}`} className="field-select h-7 text-xs py-0" value={d.siguiente?.treatment_id || ""} onChange={(e) => onSiguiente(d, e.target.value)}>
      <option value="">— Sin decidir —</option>
      {espera.map((t, i) => <option key={t.id} value={t.id}>{i + 1}. {t.patient.nombre}{t.patient.centro ? ` (${t.patient.centro})` : ""}{t.reservado && t.reservado !== d.nombre ? ` · ya en ${t.reservado}` : ""}</option>)}
    </select>
    {d.siguiente && (
      <span className="flex items-center gap-1 mt-0.5">Empieza
        <input type="date" value={d.siguiente.fecha || ""} onChange={(e) => onFecha(d, e.target.value)} data-testid={`device-siguiente-fecha-${d.id}`} className="h-6 flex-1 rounded border border-slate-200 px-1 text-[11px] text-slate-700" />
      </span>
    )}
  </label>
);

const DeviceCard = ({ d, onAssign, onReturn, onChange, espera, onSiguiente, onFecha }) => {
  const t = d.treatment;
  const warn = t && (["finalizado", "baja"].includes(t.estado) || d.retrasado);
  return (
    <div data-testid={`device-${d.nombre.replace(" ", "-").toLowerCase()}`}
      className={`rounded-lg border p-3 flex flex-col gap-2 transition-colors ${!t ? "bg-emerald-50/60 border-emerald-200" : warn ? "bg-rose-50 border-rose-200" : "bg-white border-amber-200"}`}>
      <div className="flex items-center justify-between">
        <span className="font-[Outfit] font-semibold text-slate-900 text-sm">{d.nombre}</span>
        <span className={`text-[10px] font-semibold uppercase tracking-wider ${!t ? "text-emerald-700" : warn ? "text-rose-700" : "text-amber-700"}`}>{!t ? "Libre" : d.retrasado ? "Retrasado" : warn ? "Devolver" : "Prestado"}</span>
      </div>
      {t ? (
        <>
          <Link to={`/tratamiento/${t.id}`} className="text-sm text-[#1E3A8A] hover:underline truncate" data-testid={`device-patient-${d.id}`}>{t.patient?.nombre}</Link>
          <div className="text-[11px] text-slate-500">{days(d.fecha_prestamo) < 0 ? `Empieza el ${fmtDate(d.fecha_prestamo)}` : `Desde ${fmtDate(d.fecha_prestamo)} · ${days(d.fecha_prestamo)} días`}{t.patient?.centro ? ` · ${t.patient.centro}` : ""}</div>
          <div className="text-[11px] text-slate-500">Citas {t.realizadas}/{t.total} · próx. {fmtDate(t.proxima_cita)}</div>
          <div className={`text-[11px] ${d.retrasado ? "text-rose-700 font-semibold" : "text-slate-500"}`} data-testid={`device-fin-${d.id}`}>Fin previsto {fmtDate(d.fin_prevista)}</div>
          <div className="flex gap-1">
            <Button size="sm" variant="outline" className="h-7 text-xs flex-1 px-1" data-testid={`device-return-${d.id}`} onClick={() => onReturn(d)}><Undo2 className="h-3 w-3 mr-1" />Devuelto</Button>
            <Button size="sm" variant="outline" className="h-7 text-xs flex-1 px-1" data-testid={`device-change-${d.id}`} onClick={() => onChange(d)}><ArrowLeftRight className="h-3 w-3 mr-1" />Cambiar</Button>
          </div>
          <SiguienteSelect d={d} espera={espera} onSiguiente={onSiguiente} onFecha={onFecha} />
        </>
      ) : (
        <>
          <Button size="sm" className="h-7 text-xs bg-[#D97706] hover:bg-amber-700" data-testid={`device-assign-${d.id}`} onClick={() => onAssign({ device: d, treatment: d.siguiente ? { id: d.siguiente.treatment_id } : null })}>Prestar{d.siguiente ? ` a ${d.siguiente.nombre.split(" ")[0]}` : ""}</Button>
          <SiguienteSelect d={d} espera={espera} onSiguiente={onSiguiente} onFecha={onFecha} />
        </>
      )}
    </div>
  );
};

const ChangeDialog = ({ sel, devices, onClose, onDone }) => {
  const libres = devices.filter((d) => !d.treatment);
  const [nid, setNid] = useState("");
  useEffect(() => {
    setNid("");
  }, [sel]);
  const submit = async () => {
    if (!nid) return toast.error("Elige el aparato nuevo");
    try {
      const { data } = await api.post(`/devices/${sel.id}/cambiar`, { nuevo_device_id: nid });
      toast.success(`Cambiado de ${data.de} a ${data.a}. ${data.de} queda libre.`);
      onClose();
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo cambiar");
    }
  };
  return (
    <Dialog open={!!sel} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="change-device-dialog">
        <DialogHeader><DialogTitle className="font-[Outfit]">Cambiar aparato</DialogTitle></DialogHeader>
        {sel && <p className="text-sm text-slate-600"><b>{sel.treatment?.patient?.nombre}</b> tiene ahora <b>{sel.nombre}</b>. Elige el aparato que se lleva en su lugar; se mantiene la fecha de préstamo y {sel.nombre} queda libre.</p>}
        <div className="grid grid-cols-3 gap-2" data-testid="change-device-options">
          {libres.length === 0 && <p className="col-span-3 text-sm text-slate-400">No hay aparatos libres.</p>}
          {libres.map((d) => (
            <button key={d.id} type="button" data-testid={`change-to-${d.id}`} onClick={() => setNid(d.id)}
              className={`rounded-md border px-2 py-2 text-xs font-semibold transition-colors ${nid === d.id ? "bg-[#D97706] border-[#D97706] text-white" : "bg-white border-slate-200 text-slate-700 hover:border-slate-400"}`}>{d.nombre}</button>
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="change-cancel">Cancelar</Button>
          <Button onClick={submit} data-testid="change-submit" className="bg-[#D97706] hover:bg-amber-700">Cambiar</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const AssignDialog = ({ sel, devices, espera, onClose, onDone }) => {
  const [tid, setTid] = useState("");
  const [did, setDid] = useState("");
  const [fecha, setFecha] = useState(today());
  useEffect(() => {
    if (!sel) return;
    setTid(sel.treatment?.id || espera[0]?.id || "");
    setDid(sel.device?.id || devices.find((d) => !d.treatment)?.id || "");
    setFecha(today());
  }, [sel, devices, espera]);
  const libres = devices.filter((d) => !d.treatment);
  const submit = async () => {
    if (!tid || !did) return toast.error("Elige paciente y aparato");
    try {
      await api.post(`/devices/${did}/asignar`, { treatment_id: tid, fecha });
      toast.success("Aparato prestado");
      onClose();
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo asignar");
    }
  };
  return (
    <Dialog open={!!sel} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="assign-device-dialog">
        <DialogHeader><DialogTitle className="font-[Outfit]">Prestar aparato Syntonic</DialogTitle></DialogHeader>
        <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500">Paciente (lista de espera)
          <select data-testid="assign-patient" className="field-select mt-1" value={tid} onChange={(e) => setTid(e.target.value)}>
            {espera.length === 0 && <option value="">No hay pacientes en espera</option>}
            {espera.map((t, i) => <option key={t.id} value={t.id}>{i + 1}. {t.patient.nombre} {t.patient.centro ? `(${t.patient.centro})` : ""}</option>)}
          </select>
        </label>
        <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500">Aparato
          <select data-testid="assign-device" className="field-select mt-1" value={did} onChange={(e) => setDid(e.target.value)}>
            {libres.length === 0 && <option value="">No hay aparatos libres</option>}
            {libres.map((d) => <option key={d.id} value={d.id}>{d.nombre}</option>)}
          </select>
        </label>
        <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500">Fecha de préstamo
          <Input type="date" data-testid="assign-fecha" value={fecha} onChange={(e) => setFecha(e.target.value)} className="mt-1" />
        </label>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button data-testid="assign-confirm" onClick={submit} className="bg-[#D97706] hover:bg-amber-700">Prestar</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ORDENES = [
  { id: "numero", label: "Número de aparato" },
  { id: "fin", label: "Fecha de devolución prevista" },
  { id: "prestamo", label: "Fecha de préstamo" },
];
const ordenar = (list, orden) => {
  if (orden === "numero") return list;
  const k = (d) => (orden === "fin" ? d.fin_prevista : d.fecha_prestamo) || "9999";
  return [...list].sort((a, b) => k(a).localeCompare(k(b)));
};

const Group = ({ title, list, onAssign, onReturn, onChange, espera, onSiguiente, onFecha }) => (
  <div>
    <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">{title} · {list.filter((d) => !d.treatment).length} libres de {list.length}</h3>
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
      {list.map((d) => <DeviceCard key={d.id} d={d} onAssign={onAssign} onReturn={onReturn} onChange={onChange} espera={espera} onSiguiente={onSiguiente} onFecha={onFecha} />)}
    </div>
  </div>
);

const SinContestar = ({ list }) => (
  <div data-testid="lista-sin-contestar" className="bg-white border border-slate-200 rounded-lg p-4">
    <div className="flex items-center gap-2 mb-1">
      <Clock className="h-4 w-4 text-amber-500" />
      <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-600">Presupuesto sin contestar</h3>
      <span className="ml-auto font-[JetBrains_Mono] text-xs text-slate-400">{list.length}</span>
    </div>
    <p className="text-[11px] text-slate-400 mb-2">Syntonic propuesto, presupuesto enviado y aún sin respuesta. Al aprobarlo pasan a la lista de espera.</p>
    <div className="divide-y divide-slate-100 max-h-[260px] overflow-y-auto">
      {list.length === 0 && <p className="text-sm text-slate-400 py-2">Nadie pendiente de contestar.</p>}
      {list.map((p, i) => (
        <Link key={p.id} to={`/pacientes/${p.id}`} data-testid={`sin-contestar-row-${i}`} className="flex items-center gap-2 py-2 text-sm hover:bg-slate-50 -mx-2 px-2 rounded">
          <span className="flex-1 truncate text-slate-800">{p.nombre}</span>
          <span className="text-[11px] text-slate-400">{p.centro}</span>
        </Link>
      ))}
    </div>
  </div>
);

const Espera = ({ espera, onAssign, canAssign, onMove }) => (
  <div data-testid="lista-espera" className="bg-white border border-slate-200 rounded-lg p-4">
    <div className="flex items-center gap-2 mb-3">
      <Clock className="h-4 w-4 text-slate-500" />
      <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-600">Lista de espera (aprobados)</h3>
      <span className="ml-auto font-[JetBrains_Mono] text-xs text-slate-400">{espera.length}</span>
    </div>
    <div className="divide-y divide-slate-100 max-h-[420px] overflow-y-auto">
      {espera.length === 0 && <p className="text-sm text-slate-400 py-2">Nadie en espera.</p>}
      {espera.map((t, i) => (
        <div key={t.id} data-testid={`espera-row-${i}`} className="flex items-center gap-2 py-2 text-sm">
          <span className="font-[JetBrains_Mono] text-xs text-slate-400 w-6">{i + 1}</span>
          <Link to={`/tratamiento/${t.id}`} className="flex-1 truncate text-slate-800 hover:underline">{t.patient.nombre}</Link>
          <span className="text-[11px] text-slate-400">{t.patient.centro}</span>
          {t.reservado && <span data-testid={`espera-reservado-${i}`} className="rounded bg-amber-100 text-amber-800 text-[10px] font-semibold px-1.5 py-0.5 whitespace-nowrap">Para {t.reservado}</span>}
          <div className="flex flex-col">
            <button data-testid={`espera-up-${i}`} disabled={i === 0} onClick={() => onMove(t.id, -1)} className="text-slate-400 hover:text-slate-900 disabled:opacity-20" title="Subir"><ChevronUp className="h-3.5 w-3.5" /></button>
            <button data-testid={`espera-down-${i}`} disabled={i === espera.length - 1} onClick={() => onMove(t.id, 1)} className="text-slate-400 hover:text-slate-900 disabled:opacity-20" title="Bajar"><ChevronDown className="h-3.5 w-3.5" /></button>
          </div>
          <Button size="sm" variant="outline" className="h-7 text-xs" disabled={!canAssign} data-testid={`espera-assign-${i}`} onClick={() => onAssign({ treatment: t })}>Prestar</Button>
        </div>
      ))}
    </div>
  </div>
);

export const SyntonicDevices = ({ onChange, reloadKey }) => {
  const { centro } = useCentro();
  const [devices, setDevices] = useState([]);
  const [espera, setEspera] = useState([]);
  const [esperaAll, setEsperaAll] = useState([]);
  const [sinContestar, setSinContestar] = useState([]);
  const [sel, setSel] = useState(null);
  const [chg, setChg] = useState(null);
  const load = useCallback(() => {
    api.get("/devices").then((r) => setDevices(r.data));
    api.get("/devices/espera", { params: { centro } }).then((r) => setEspera(r.data));
    api.get("/devices/espera").then((r) => setEsperaAll(r.data));
    api.get("/devices/sin-contestar", { params: { centro } }).then((r) => setSinContestar(r.data));
  }, [centro]);
  useEffect(() => {
    load();
  }, [load, reloadKey]);
  const refresh = () => { load(); onChange?.(); };
  const onReturn = async (d) => {
    if (!window.confirm(`¿Confirmas que ${d.treatment?.patient?.nombre} ha devuelto ${d.nombre}?`)) return;
    await api.post(`/devices/${d.id}/devolver`, { fecha: today() });
    toast.success(`${d.nombre} vuelve a estar libre`);
    refresh();
  };
  const onMove = async (tid, direccion) => {
    await api.post("/devices/espera/mover", { treatment_id: tid, direccion });
    load();
  };
  const onSiguiente = async (d, tid) => {
    await api.post(`/devices/${d.id}/siguiente`, { treatment_id: tid });
    toast.success(tid ? `Siguiente para ${d.nombre} apuntado` : `${d.nombre} sin siguiente`);
    load();
  };
  const onFecha = async (d, fecha) => {
    await api.post(`/devices/${d.id}/siguiente-fecha`, { fecha });
    load();
  };
  const [orden, setOrden] = useState("numero");
  const colier = ordenar(devices.filter((d) => d.modelo === "COLIER"), orden);
  const silvia = ordenar(devices.filter((d) => d.modelo === "SILVIA"), orden);
  const libres = devices.filter((d) => !d.treatment).length;
  return (
    <div data-testid="syntonic-devices" className="mb-8 fade-up">
      <div className="flex items-center gap-2 mb-3">
        <Sun className="h-4 w-4 text-amber-600" />
        <h2 className="font-[Outfit] text-lg font-semibold text-slate-900">Control de aparatos</h2>
        <span className="text-xs text-slate-500" data-testid="devices-summary">{libres} libres de {devices.length} · {espera.length} en espera</span>
        <label className="ml-auto flex items-center gap-2 text-xs text-slate-500 whitespace-nowrap">Ordenar por
          <select data-testid="devices-orden" className="field-select h-8 text-xs py-0 w-auto" value={orden} onChange={(e) => setOrden(e.target.value)}>
            {ORDENES.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
        </label>
        <Link to="/syntonic/imprimir-aparatos" target="_blank" data-testid="devices-print-btn" className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs text-slate-700 hover:bg-slate-50"><Printer className="h-3.5 w-3.5" />Imprimir control de aparatos</Link>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_340px] gap-4">
        <div className="space-y-4">
          <Group title="Colier" list={colier} onAssign={setSel} onReturn={onReturn} onChange={setChg} espera={esperaAll} onSiguiente={onSiguiente} onFecha={onFecha} />
          <Group title="Silvia" list={silvia} onAssign={setSel} onReturn={onReturn} onChange={setChg} espera={esperaAll} onSiguiente={onSiguiente} onFecha={onFecha} />
        </div>
        <div className="space-y-4">
          <Espera espera={espera} onAssign={setSel} canAssign={libres > 0} onMove={onMove} />
          <SinContestar list={sinContestar} />
        </div>
      </div>
      <AssignDialog sel={sel} devices={devices} espera={esperaAll} onClose={() => setSel(null)} onDone={refresh} />
      <ChangeDialog sel={chg} devices={devices} onClose={() => setChg(null)} onDone={refresh} />
    </div>
  );
};
